import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { IMAGES, DIFFICULTY, CLEANUP_UPSELLS } from "../lib/site";

const CardShell = ({ className = "", children, testid }) => (
    <div
        data-testid={testid}
        className={`group relative overflow-hidden rounded-lg border border-brand-line bg-brand-surface ${className}`}
    >
        {children}
    </div>
);

const ServicesBento = () => (
    <section className="max-w-7xl mx-auto px-5 sm:px-8 py-24" data-testid="services-section">
        <Reveal className="mb-14 flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
                <Eyebrow className="mb-4">What we do</Eyebrow>
                <h2 className="font-display font-black uppercase text-brand-cream text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                    Grind it. Clean it. <span className="text-brand-amber">Done.</span>
                </h2>
            </div>
            <p className="max-w-sm text-brand-sage text-base leading-relaxed">
                One crew, one visit — from the first cut of the blade to the
                last rake pass over your lawn.
            </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-[minmax(220px,auto)] gap-5">
            <Reveal className="md:col-span-2" delay={0}>
                <CardShell testid="service-card-grinding" className="h-full">
                    <div className="absolute inset-0">
                        <img
                            src={IMAGES.chainsawLogs}
                            alt="Professional stump grinding equipment on a job site"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/60 to-transparent" />
                    </div>
                    <div className="relative z-10 h-full flex flex-col justify-end p-7 sm:p-9">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber mb-3">
                            01 — Core Service
                        </p>
                        <h3 className="font-display font-black uppercase text-brand-cream text-3xl sm:text-4xl tracking-tight mb-3">
                            Stump Grinding
                        </h3>
                        <p className="text-brand-sage text-sm sm:text-base leading-relaxed max-w-md">
                            Surface roots traced and cut. Grindings raked back
                            into the hole as our standard — never an upcharge.
                        </p>
                    </div>
                </CardShell>
            </Reveal>

            <Reveal delay={0.08}>
                <CardShell testid="service-card-guarantee" className="h-full bg-brand-amber border-brand-amber">
                    <div className="relative z-10 h-full flex flex-col justify-between p-7 sm:p-9">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-ink/70">
                            02 — Our Promise
                        </p>
                        <div>
                            <p className="font-display font-black uppercase text-brand-ink text-5xl sm:text-6xl tracking-tight leading-[0.9]">
                                Same-Week
                            </p>
                            <p className="font-display font-black uppercase text-brand-ink text-5xl sm:text-6xl tracking-tight leading-[0.9]">
                                Guarantee
                            </p>
                            <p className="mt-4 text-brand-ink/80 text-sm sm:text-base">
                                Call this week, we grind this week. That's the
                                deal, every time.
                            </p>
                        </div>
                    </div>
                </CardShell>
            </Reveal>

            <Reveal delay={0.05}>
                <CardShell testid="service-card-cleanup" className="h-full">
                    <div className="absolute inset-0">
                        <img
                            src={IMAGES.woodChips}
                            alt="Fresh wood chips from stump grinding"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-brand-ink via-brand-ink/55 to-transparent" />
                    </div>
                    <div className="relative z-10 h-full flex flex-col justify-end p-7 sm:p-9">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber mb-3">
                            03 — Add-Ons
                        </p>
                        <h3 className="font-display font-black uppercase text-brand-cream text-3xl sm:text-4xl tracking-tight mb-3">
                            Cleanup &amp; Restoration
                        </h3>
                        <ul className="space-y-1.5">
                            {CLEANUP_UPSELLS.map((c) => (
                                <li
                                    key={c.label}
                                    className="flex justify-between gap-3 font-mono text-xs sm:text-sm text-brand-sage border-b border-brand-cream/10 pb-1.5"
                                >
                                    <span>{c.label}</span>
                                    <span className="text-brand-cream">{c.add}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </CardShell>
            </Reveal>

            <Reveal className="md:col-span-2" delay={0.12}>
                <CardShell testid="service-card-difficulty" className="h-full">
                    <div className="absolute inset-0">
                        <img
                            src={IMAGES.woodSlab}
                            alt="Fresh cross-section of timber showing grain"
                            className="w-full h-full object-cover opacity-30 transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-ink via-brand-ink/90 to-brand-ink/60" />
                    </div>
                    <div className="relative z-10 h-full flex flex-col justify-center p-7 sm:p-9">
                        <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brand-amber mb-3">
                            04 — No Job Too Awkward
                        </p>
                        <h3 className="font-display font-black uppercase text-brand-cream text-3xl sm:text-4xl tracking-tight mb-5">
                            Every Situation Priced Upfront
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {DIFFICULTY.slice(1).map((d) => (
                                <span
                                    key={d.label}
                                    className="font-mono text-[11px] uppercase tracking-wider border border-brand-line bg-brand-ink/70 rounded-full px-3.5 py-1.5 text-brand-sage"
                                >
                                    {d.label}
                                </span>
                            ))}
                        </div>
                        <Link
                            to="/pricing"
                            data-testid="services-pricing-link"
                            className="mt-7 inline-flex items-center gap-2 text-brand-amber font-semibold hover:gap-3 transition-all duration-300"
                        >
                            See the full price list
                            <ArrowUpRight className="w-4 h-4" />
                        </Link>
                    </div>
                </CardShell>
            </Reveal>
        </div>
    </section>
);

export default ServicesBento;
